import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { stringify } from 'query-string';
import { useCss, IStyles } from 'app/utils/css';
import { getWindowOrThrow } from 'app/utils/env';
import { getPasswordValidationErrors } from 'app/components/auth/PasswordRules';
import TermsAgreement from 'app/components/auth/TermsAgreement';
import { useQuery } from 'app/components/Contexts';
import { useI18n } from 'app/components/localization';
import RedirectingPage from 'app/components/pages/RedirectingPage';
import LoadingButtonA from 'app/components/presentation/buttons/LoadingButtonA';
import FacebookButton from 'app/components/presentation/buttons/FacebookButton';
import { USER as USER_PASSWORD_RULES } from 'app/constants/enums/PasswordType';
import { User } from 'app/constants/schemas/User';
import EntityField from 'app/containers/entities/EntityField';
import { PURE_WHITE } from 'app/constants/Colors';
import { LoginRoute } from 'app/constants/Routes';
import { MAX_FIELD_LENGTH, DISPLAY_NAME_MAX_LENGTH } from 'app/constants/UserFieldRestrictions';
import Link from 'app/components/presentation/link/Link';
import {
  InputField,
  PasswordField,
  CheckboxField,
} from 'app/components/presentation/forms/Fields';
import Form from 'app/components/forms/Form';
import { LoadingStage } from 'app/containers/state/LoadingState';
import {
  AUTH_BUTTON_CONTAINER_STYLE,
  AUTH_ERROR_STYLE,
  AUTH_SEPARATOR_TEXT,
} from './AuthStyles';
import Separator from 'app/components/layout/Separator';
import { useHistory } from 'app/components/router/History';
import { useIFrame } from 'app/components/utilities/IFrameContext';
import { getAllErrorMessages } from 'app/utils/serverErrorMessages';
import LanguageAndTimezoneField from 'app/components/users/LanguageAndTimezoneField';
import InputMarginGroup from 'app/components/auth/InputMarginGroup';
import FormSectionCard from 'app/components/presentation/containers/FormSectionCard';
import PageWidth from 'app/components/presentation/containers/page/PageWidth';
import { useScrollCtrl } from 'app/components/scroll/ScrollContext';
import { getLanguageById } from 'app/constants/enums/Language';
import { getWithDefault } from 'app/utils/fpTs';
import { parseBoolean } from 'app/utils/parse';
import { LanguageId } from 'app/constants/enums/Language/data';
import { useBrand } from '../OrganizationContext';
import { EntityStringId } from 'app/types/entity';

interface IProps {
  onSubmitCredentials: (id: string) => void;
  onSubmitFacebook: (url: string) => void;
  id: EntityStringId;
  credentialsLoadingState: LoadingStage;
  facebookLoadingState: LoadingStage;
  error: any;
}

const SignupForm: React.SFC<IProps> = ({
  onSubmitCredentials,
  onSubmitFacebook,
  id,
  credentialsLoadingState,
  facebookLoadingState,
  error,
}) => {
  const { t, tx } = useI18n();
  const css = useCss();
  const brand = useBrand();
  const history = useHistory();
  const scrollCtrl = useScrollCtrl();
  const query = useQuery();
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const { isIFrame: embeddableVariant } = useIFrame();
  const autoFB = getWithDefault(parseBoolean(query.autoFB), false);

  const shouldScrollToError = useRef(false);
  const errorRef = useRef(null);
  const passwordErrorRef = useRef<React.ReactNode>(undefined);

  const handleFacebookSubmit = () => {
    const { pathname, search, hash } = history.location;
    return onSubmitFacebook(`${pathname}${search}${hash}`);
  };

  const redirectFacebookSubmit = () => {
    getWindowOrThrow().top.location.href = `${history.location.pathname}?autoFB=true`;
  };

  useEffect(() => {
    if (autoFB) {
      handleFacebookSubmit();
    }
    // Providing empty array because this code is only run once "on mount"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    shouldScrollToError.current = true;
    return onSubmitCredentials(id);
  };

  function renderContents() {
    return (
      <InputMarginGroup>
        {renderFacebookAuthBox()}
        <Separator>
          <span className={css('orSeparatorText', AUTH_SEPARATOR_TEXT)}>{t('auth.signup-form.or-separator')}</span>
        </Separator>
        {renderCredentialsSignupForm()}
      </InputMarginGroup>
    );
  }

  function renderCredentialsSignupForm() {
    const disabled =
      credentialsLoadingState === LoadingStage.Success ||
      credentialsLoadingState === LoadingStage.Loading;
    const credentialsError = credentialsLoadingState === LoadingStage.Error;
    if (credentialsLoadingState === LoadingStage.Error && shouldScrollToError.current) {
      scrollToError();
      shouldScrollToError.current = false;
    }
    return (
      <Form disabled={disabled} onSubmit={validate(handleSubmit)}>
        <InputMarginGroup>
          {credentialsError && (
            <div ref={errorRef} className={css('error', AUTH_ERROR_STYLE)}>
              <pre
                className={css('preText', {
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                })}
              >
                {getAllErrorMessages(t, error)}
              </pre>
            </div>)}

          <EntityField id={id} field="name" type={User}>{({ value, setValue }) => (
            <InputField
              label={t('auth.input.placeholder.name')}
              description={t('admin.users.user-form.name-description')}
              value={value}
              onChange={setValue}
              maxLength={MAX_FIELD_LENGTH}
              required
            />
          )}</EntityField>

          <EntityField id={id} field="displayName" type={User}>{({ value, setValue }) => (
            <InputField
              label={t('auth.input.placeholder.username')}
              description={t('admin.users.user-form.display-name-description')}
              value={value}
              onChange={setValue}
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              required
            />
          )}</EntityField>

          <EntityField id={id} field="email" type={User}>{({ value, setValue }) => (
            <InputField
              label={t('auth.input.placeholder.email')}
              value={value}
              onChange={setValue}
              type="email"
              required
            />
          )}</EntityField>

          <EntityField id={id} field="password" type={User}>{({ value, setValue }) => (
            <PasswordField
              label={t('auth.input.placeholder.password')}
              value={value}
              onChange={(newValue: string) => {
                passwordErrorRef.current = getPasswordValidationErrors(t, newValue, USER_PASSWORD_RULES);
                setValue(newValue);
              }}
              required
              validationError={showValidationErrors && passwordErrorRef.current}
            />
          )}</EntityField>

          <EntityField id={id} field="language" type={User}>
            {({ value: language, setValue: setLanguage }) =>
              <EntityField id={id} field="timezone" type={User}>
                {({ value: timezone, setValue: setTimezone }) =>
                  <LanguageAndTimezoneField
                    language={language!}
                    setLanguage={(languageId: LanguageId) => setLanguage(getLanguageById(languageId))}
                    timezone={timezone!}
                    setTimezone={setTimezone}
                  />
                }
              </EntityField>
            }
          </EntityField>

          <TermsAgreement
            showValidationErrors={showValidationErrors}
            setAgree={setAgreeChecked}
          />

          <EntityField id={id} field="marketing_email_consent" type={User}>{({ value, setValue }) => (
            <CheckboxField
              label={t('auth.signup-form.promotions-checkbox', { market: brand.name })}
              onToggle={setValue}
              checked={value}
            />
          )}</EntityField>

          <div
            className={css('buttonContainer', {
              ...AUTH_BUTTON_CONTAINER_STYLE,
              justifyContent: 'center',
            })}
          >
            <LoadingButtonA
              isLoading={credentialsLoadingState === LoadingStage.Loading}
              disabled={
                disabled ||
                facebookLoadingState === LoadingStage.Loading
              }
              type="submit"
            >
              {credentialsLoadingState === LoadingStage.Success ?
              t('auth.signup-form.signup-complete-button') :
              t('auth.signup-form.submit')}
            </LoadingButtonA>
          </div>
        </InputMarginGroup>
      </Form>
    );
  }

  function renderFacebookAuthBox() {
    const facebookError = facebookLoadingState === LoadingStage.Error;
    return (
      <InputMarginGroup>
        {facebookError && (
          <div className={css('error', AUTH_ERROR_STYLE)}>
            {error ? error.cause.localizedErrorMessage(t) || error.message : ''}
          </div>)}
        <FacebookButton
          isLoading={facebookLoadingState === LoadingStage.Loading}
          onClick={embeddableVariant ? redirectFacebookSubmit : handleFacebookSubmit}
          fullWidth
          children={t('auth.signup-form.signup-facebook')}
          disabled={credentialsLoadingState === LoadingStage.Loading}
        />
      </InputMarginGroup>
    );
  }

  const scrollToError = () => {
    // Wait until error is actually rendered; otherwise, it won't be found.
    setTimeout(() => {
      if (errorRef) {
        const element = errorRef.current;
        if (element) {
          scrollCtrl.scrollToElement(element, -20); // add -20 offset to the top to add some space above error
        }
      }
    }, 100);
  };

  const validate = (load: any) => () => {
    if (!agreeChecked || passwordErrorRef.current) {
      setShowValidationErrors(true);
    } else {
      load();
    }
  };

  return (autoFB
    ? <RedirectingPage />
    : embeddableVariant
    ? (
      <PageWidth maxWidth="396px">
        <div className={css('embeddableVariant', embeddableVariantStyle)}>
          {renderContents()}
        </div>
      </PageWidth>
    ) : (
      <FormSectionCard
        header={t('auth.signup-form.title')}
        headerActionItem={
          tx('auth.signup-form.signin-link', {
            link: (
              <Link to={`${LoginRoute.getPathname()}?${stringify(query)}`}>
                {t('auth.signup-form.signin')}
              </Link>
            ),
          })
        }
      >
        {renderContents()}
      </FormSectionCard>
    )
  );
};

const embeddableVariantStyle: IStyles = {
  padding: '3em',
  boxSizing: 'border-box',
  display: 'flex',
  flex: 'auto',
  flexDirection: 'column',
  margin: '0 auto',
  fontSize: '14px',
  backgroundColor: PURE_WHITE,
};

export default SignupForm;
